import { SUPPLIER_BACKEND_API_ENDPOINTS } from "../config/apiConfig";
import { apiClient } from "./api-client.service";

import type {
  SupplierBlackout,
  SupplierBlackoutFilter,
  SupplierBlackoutPayload,
  SupplierBlackoutResponse,
} from "../types/supplier-blackouts.types";

type SupplierBlackoutDetailResponse = {
  success: boolean;
  statusCode: number;
  message: string;
  resData: SupplierBlackout;
};

type SupplierBlackoutMutationResponse = {
  success: boolean;
  statusCode: number;
  message: string;
  resData: unknown;
};

const endpoints = SUPPLIER_BACKEND_API_ENDPOINTS.supplierInventoryBlackout;

const failure = (error: unknown, fallback: string) => ({
  success: false,
  statusCode: 500,
  message: error instanceof Error ? error.message : fallback,
  resData: null,
});

export const supplierBlackoutsService = {
  async list(filter: SupplierBlackoutFilter): Promise<SupplierBlackoutResponse> {
    try {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, String(value));
        }
      });

      return await apiClient<SupplierBlackoutResponse>(endpoints.list(params.toString()), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });
    } catch (error) {
      return failure(
        error,
        "Failed to load supplier blackouts due to an unexpected error.",
      ) as unknown as SupplierBlackoutResponse;
    }
  },

  async details(id: number | string): Promise<SupplierBlackoutDetailResponse> {
    try {
      return await apiClient<SupplierBlackoutDetailResponse>(endpoints.details(id), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });
    } catch (error) {
      return failure(
        error,
        "Failed to load supplier blackout details due to an unexpected error.",
      ) as unknown as SupplierBlackoutDetailResponse;
    }
  },

  // only supplier_code is required, every other field is optional
  async create(
    payload: Pick<SupplierBlackoutPayload, "supplier_code"> & Partial<SupplierBlackoutPayload>,
  ): Promise<SupplierBlackoutMutationResponse> {
    try {
      return await apiClient<SupplierBlackoutMutationResponse>(endpoints.create, {
        method: "POST",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to create supplier blackout due to an unexpected error.",
      ) as SupplierBlackoutMutationResponse;
    }
  },

  async update(
    id: number | string,
    payload: Partial<SupplierBlackoutPayload>,
  ): Promise<SupplierBlackoutMutationResponse> {
    try {
      return await apiClient<SupplierBlackoutMutationResponse>(endpoints.update(id), {
        method: "PATCH",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to update supplier blackout due to an unexpected error.",
      ) as SupplierBlackoutMutationResponse;
    }
  },

  async updateStatus(
    id: number | string,
    payload: { status: "active" | "inactive" },
  ): Promise<SupplierBlackoutMutationResponse> {
    return supplierBlackoutsService.update(id, payload);
  },

  async delete(id: number | string): Promise<SupplierBlackoutMutationResponse> {
    try {
      return await apiClient<SupplierBlackoutMutationResponse>(endpoints.delete(id), {
        method: "DELETE",
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to delete supplier blackout due to an unexpected error.",
      ) as SupplierBlackoutMutationResponse;
    }
  },
};
